import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import Header from 'quill/formats/header';
import React, { useEffect, useMemo } from 'react';
import HeaderId from '../editor/formats/HeaderId';
import DelayedRenderBlockGroup from '../editor/formats/namespaces/postPreview/DelayedRenderBlockGroup';
import QuillComponent from '../editor/QuillComponent';
import type QuillComponentProps from '../editor/QuillComponentProps';
import { useQuill } from '../editor/QuillContext';
import type { TableDocument } from '../editor/table/types';
import useHeaderCollapse from '../editor/useHeaderCollapse';
import type { RenderBlock } from '../editor/utils/deltaToRenderBlocks';
import deltaToRenderBlocks, {
  renderBlocksToDelta,
} from '../editor/utils/deltaToRenderBlocks';

const SCROLL_MARGIN = 10;
const MAX_COMPLEXITY_PER_GROUP = 1000;

/*
 * Group render blocks by complexity to serve as unit blocks for lazy loading.
 * Headers are exceptions and are always rendered immediately to support TOC and anchor scrolling.
 */
const groupByComplexity = (renderBlocks: RenderBlock[]) => {
  const result: (RenderBlock | RenderBlock[])[] = [];

  let currentGroup: RenderBlock[] = [];
  let currentComplexity = 0;

  const fulfillCurrentGroup = () => {
    if (currentGroup.length) {
      result.push(currentGroup);
      currentGroup = [];
      currentComplexity = 0;
    }
  };

  renderBlocks.forEach((renderBlock) => {
    if (renderBlock.attributes['header-id']) {
      fulfillCurrentGroup();
      result.push(renderBlock);
    } else {
      currentGroup.push(renderBlock);
      currentComplexity += computeComplexity(renderBlock);
      if (currentComplexity > MAX_COMPLEXITY_PER_GROUP) {
        fulfillCurrentGroup();
      }
    }
  });

  fulfillCurrentGroup();

  return result;
};

const groupNonAnchorRenderBlocksByComplexity = (
  blocks: RenderBlock[],
  anchorHeaderId: string | undefined,
) => {
  const result: (RenderBlock | RenderBlock[])[] = [];

  const anchorIndex = Math.max(
    anchorHeaderId
      ? blocks.findIndex(
          ({ attributes }) => attributes['header-id'] === anchorHeaderId,
        )
      : -1,
    0, // Defaults to the first block if the anchor is not found
  );
  const blocksBeforeAnchor = blocks.slice(0, anchorIndex);
  const blocksForAnchor = blocks.slice(anchorIndex, anchorIndex + 200);
  const blocksAfterAnchor = blocks.slice(anchorIndex + 200);

  result.push(
    ...groupByComplexity(blocksBeforeAnchor),
    ...blocksForAnchor, // Avoid lazy loading for the anchor block since it's the initial view
    ...groupByComplexity(blocksAfterAnchor),
  );

  return result;
};

const findScrollingContainer = (element: HTMLElement) => {
  let current: HTMLElement | null = element;
  while (current) {
    const overflow = getComputedStyle(current).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') {
      return current;
    }

    current = current.parentElement;
  }

  return null;
};

const computeComplexity = (block: RenderBlock) => {
  const attributeComplexity = Object.keys(block.attributes).length * 2;
  if (block.type === 'text') {
    return block.line.reduce(
      (sum, op) =>
        sum + (op.attributes ? Object.keys(block.attributes).length : 0),
      attributeComplexity,
    );
  }

  let embedComplexity = 1;
  if (block.key === 'table-embed') {
    const table = block.value as TableDocument;
    embedComplexity = new Delta(table.rows).length();
  } else if (block.key === 'code-embed') {
    embedComplexity = 10;
  }

  return attributeComplexity + embedComplexity;
};

interface PostPreviewQuillComponentProps
  extends Omit<
    QuillComponentProps,
    'readOnly' | 'namespace' | 'content' | 'initialContent' | 'quillRef'
  > {
  content: Delta | Op[];
  anchor?: string;
  namespace?: 'postPreview' | 'embedPostContent';
}

const PostPreviewQuillComponent = ({
  content,
  anchor,
  rootClassName,
  namespace = 'postPreview',
  ...props
}: PostPreviewQuillComponentProps) => {
  const { quill } = useQuill();
  useHeaderCollapse(quill, { skip: namespace === 'postPreview' });

  const anchorHeaderId = anchor?.split('-')[0];
  const delta = useMemo(() => {
    const [titleBlock, ...resetBlocks] = deltaToRenderBlocks(
      new Delta(content),
    );
    if (!titleBlock) return new Delta().insert('\n');

    return groupNonAnchorRenderBlocksByComplexity(
      resetBlocks,
      anchorHeaderId,
    ).reduce(
      (acc, group) =>
        Array.isArray(group)
          ? acc.insert({ [DelayedRenderBlockGroup.blotName]: group })
          : acc.concat(renderBlocksToDelta([group])),
      renderBlocksToDelta([titleBlock]),
    );
  }, [content, anchorHeaderId]);

  useEffect(() => {
    if (!anchorHeaderId || !quill) return undefined;
    const header = quill.scroll
      .descendants(Header)
      .find((h) => HeaderId.value(h.domNode) === anchorHeaderId);

    const scrollingContainer = header && findScrollingContainer(header.domNode);
    if (!scrollingContainer) return undefined;

    const scrollToAnchor = () => {
      // Element#scrollIntoView() does not work due to CSS transform scaling.
      const containerTop = scrollingContainer.getBoundingClientRect().top;
      const selfTop = header.domNode.getBoundingClientRect().top;

      scrollingContainer.scrollTop += selfTop - containerTop - SCROLL_MARGIN;
    };

    const observer = new ResizeObserver(scrollToAnchor);
    observer.observe(scrollingContainer);
    return () => {
      observer.disconnect();
    };
  }, [content, anchorHeaderId, quill]);

  return (
    <QuillComponent
      content={delta}
      rootClassName={rootClassName}
      contentPlaceholder=""
      namespace={namespace}
      readOnly
      smallTitleSize
      titlePlaceholder="Untitled"
      {...props}
    />
  );
};

export default PostPreviewQuillComponent;
